import { Button, Kind } from '@thanx/uikit/button'
import palette from 'constants/palette'
import React from 'react'
import { useStyletron } from 'styletron-react'

export interface TourPagerLabels {
  // Pre-translated (the pager is i18n-agnostic; WizardTour owns t()).
  dotLabel: (step: number, total: number) => string
  // The current step's CTA text, already resolved by WizardTour (per-step: e.g.
  // "Next", "Okay, show me", "Got it"). The pager still decides finish-vs-advance
  // from isLast — a custom mid-tour label advances, the last step finishes.
  cta: string
}

interface TourPagerProps {
  // Step ids key the dots (NEVER the array index) — stable across reorders.
  stepIds: string[]
  currentIndex: number
  labels: TourPagerLabels
  onGoTo: (index: number) => void
  onNext: () => void
  onFinish: () => void
}

export const DOT_SIZE_PX = 8
// Padding around each dot. Keeps the tap target at 24px square (dot + 2×8) while
// staying far narrower than uikit's STANDARD 24px side padding — see the dot
// overrides below.
export const DOT_PADDING_PX = 8

// The tour card is dark (grey90); the CTA is the Figma teal. uikit's Button
// spreads a consumer `overrides.BaseButton` LAST, REPLACING its own kind/size
// preset — so a partial override would strip padding/radius/font. We therefore
// supply the COMPLETE button styling here (a documented consequence of the
// uikit override contract), keeping every value on a palette token.
export const ctaButtonOverrides = {
  BaseButton: {
    style: {
      backgroundColor: palette.spearmint50,
      color: palette.white,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingLeft: '16px',
      paddingRight: '16px',
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '16px',
      ':hover': { backgroundColor: palette.spearmint50Hover },
      // The CTA must never be squeezed out of the card: keep it at its natural
      // width on one line and let the dot row wrap instead (ENG-967).
      flexShrink: 0,
      whiteSpace: 'nowrap' as const,
    },
  },
}

// ENG-967 root cause: a dot Button inherited uikit's STANDARD size padding
// (1.5rem each side), so five 56px-wide dots + the CTA overflowed the card's
// 320px content box and the CTA rendered OUTSIDE the card. Same complete-override
// contract as the CTA above — supplying BaseButton replaces the kind preset, so
// every value the dot needs is spelled out here (transparent minimal surface +
// a tight, 24px-square tap target).
export const dotButtonOverrides = {
  BaseButton: {
    style: {
      backgroundColor: 'transparent',
      borderWidth: '0',
      padding: `${DOT_PADDING_PX}px`,
      lineHeight: '0',
      ':hover': { backgroundColor: 'transparent' },
    },
  },
}

// Dot pager + Next/Done CTA for the coach-mark tours. Per Figma: dots sit at the
// bottom-LEFT, the CTA at the bottom-RIGHT, and there is NO Back / Skip control.
// Every control is a real @thanx/uikit Button (keyboard-activatable); each dot
// carries an i18n aria-label + aria-current, with a decorative (aria-hidden)
// visual dot. On the dark card, upcoming dots use grey50 (visible on grey90) and
// the active dot uses the teal accent. i18n-agnostic; NOT a React.FC.
export const TourPager = ({
  stepIds,
  currentIndex,
  labels,
  onGoTo,
  onNext,
  onFinish,
}: TourPagerProps): JSX.Element => {
  const [css] = useStyletron()
  const totalSteps = stepIds.length
  const isLast = currentIndex === totalSteps - 1

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          // minWidth:0 lets the dot row shrink inside the flex parent instead of
          // pushing the CTA past the card's edge (ENG-967).
          minWidth: 0,
          gap: '6px',
        })}
      >
        {stepIds.map((id, i) => (
          <Button
            key={id}
            kind={Kind.MINIMAL}
            overrides={dotButtonOverrides}
            aria-label={labels.dotLabel(i + 1, totalSteps)}
            aria-current={i === currentIndex ? 'step' : undefined}
            onClick={() => onGoTo(i)}
          >
            <span
              aria-hidden="true"
              className={css({
                display: 'block',
                width: `${DOT_SIZE_PX}px`,
                height: `${DOT_SIZE_PX}px`,
                borderRadius: '50%',
                backgroundColor:
                  i === currentIndex ? palette.spearmint50 : palette.grey50,
              })}
            />
          </Button>
        ))}
      </div>

      <Button
        kind={Kind.PRIMARY}
        overrides={ctaButtonOverrides}
        onClick={isLast ? onFinish : onNext}
      >
        {labels.cta}
      </Button>
    </div>
  )
}
